import {Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {RouterLink} from '@angular/router';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {CouponService} from '../../../services/coupon.service';
import {CouponBenefits, CouponRedemption} from '../../../models/coupon';

/** What the green success card shows - straight from the 201 body. */
interface RedeemSuccess {
  message: string;
  title: string;
  details: string[];
}

/**
 * SCRUM-311 + 328 - "יש לך קוד הטבה?": activate a 9-digit coupon code.
 *
 * - Digits only, exactly 9 (leading zeros kept - the code is a string).
 * - One request at a time: the button is disabled while sending.
 * - Error -> the server `message` in red, no sound.
 * - Success ONLY on HTTP 201 -> green card (role=status, aria-live) with a
 *   short Web Audio chime; the benefits list refreshes from the response.
 * - Hidden when the server says `features.redemption === false`.
 */
@Component({
  selector: 'app-coupon-activation',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './coupon-activation.component.html',
  styleUrls: ['./coupon-activation.component.scss'],
})
export class CouponActivationComponent implements OnInit, OnDestroy {
  /** Load GET my/ for the feature flag (the benefits page loads it itself). */
  @Input() autoLoad = true;
  /** Show a "ההטבות שלי" link in the success card (not on the benefits page). */
  @Input() showBenefitsLink = true;

  readonly CODE_LENGTH = 9;

  code = '';
  touched = false;
  sending = false;
  error = '';
  success: RedeemSuccess | null = null;
  redemptionEnabled = true;

  private sub?: Subscription;
  private readonly isBrowser: boolean;

  constructor(private coupons: CouponService, @Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.sub = this.coupons.benefits$.subscribe((b: CouponBenefits | null) => {
      // Unknown (not loaded / failed) = show the form; the server is the gate.
      this.redemptionEnabled = b?.features?.redemption !== false;
    });
    if (this.autoLoad && this.isBrowser) {
      this.coupons.loadMyBenefits().subscribe({error: () => {}});
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get codeValid(): boolean {
    return new RegExp('^\\d{' + this.CODE_LENGTH + '}$').test(this.code);
  }

  get showValidation(): boolean {
    return this.touched && !this.codeValid;
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = String(input.value || '').replace(/\D/g, '').slice(0, this.CODE_LENGTH);
    if (input.value !== digits) input.value = digits;
    this.code = digits;
    if (this.error) this.error = '';
  }

  submit(event?: Event): void {
    event?.preventDefault();
    if (this.sending) return;
    this.touched = true;
    if (!this.codeValid) return;

    this.sending = true;
    this.error = '';
    this.success = null;
    this.coupons.redeem(this.code).pipe(finalize(() => this.sending = false)).subscribe({
      next: res => {
        const body = res.body;
        if (res.status !== 201 || !body) {
          // Anything but a confirmed redemption is not a success.
          this.error = body?.message || 'לא הצלחנו להפעיל את הקופון. נסו שוב.';
          return;
        }
        const r: CouponRedemption | undefined = body.redemption;
        this.success = {
          message: body.message || 'הקופון הופעל בהצלחה!',
          title: r?.title || '',
          details: Array.isArray(body.details) ? body.details.filter(Boolean) : [],
        };
        this.code = '';
        this.touched = false;
        this.playSuccessChime();
        if (!body.benefits) {
          this.coupons.loadMyBenefits().subscribe({error: () => {}});
        }
      },
      error: err => {
        this.error = this.coupons.errorMessage(err, 'לא הצלחנו להפעיל את הקופון. בדקו את החיבור ונסו שוב.');
      },
    });
  }

  dismissSuccess(): void {
    this.success = null;
  }

  /**
   * Two short, quiet sine tones (~250 ms). Created only here - inside the
   * click's async continuation - so autoplay policies allow it. Never throws.
   */
  private playSuccessChime(): void {
    if (!this.isBrowser) return;
    try {
      const w = window as any;
      const Ctx = w.AudioContext || w.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const start = ctx.currentTime + 0.01;
      const tone = (freq: number, at: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, at);
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.08, at + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(at);
        osc.stop(at + dur + 0.02);
      };
      tone(784, start, 0.11);         // G5
      tone(1046.5, start + 0.12, 0.13); // C6
      if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
      setTimeout(() => { try { ctx.close?.(); } catch (e) { /* ignore */ } }, 600);
    } catch (e) {
      // Sound is a nicety - never break the flow.
    }
  }
}
